import { useEffect, useMemo, useState } from "react";
import {
  useGetCategoriesQuery,
  useGetCategoryQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
} from "../../../../redux/api";
import type { Category as CategoryModel } from "../../../../redux/api";
import { Modal } from "../../../../components/Modal";
import { EntityMeta } from "../../../../components/EntityMeta";
import { Field } from "../../../../components/Field";
import { statusTone } from "../../../../utils/status";

interface CategoryFormModalProps {
  open: boolean;
  /** null = create */
  id: string | null;
  onClose: () => void;
}

type CategoryForm = {
  name: string;
  code: string;
  description: string;
  parentId: string;
  status: string;
};

const emptyForm: CategoryForm = {
  name: "",
  code: "",
  description: "",
  parentId: "",
  status: "active",
};

const formFromCategory = (c: CategoryModel): CategoryForm => ({
  name: c.name ?? "",
  code: c.code ?? "",
  description: c.description ?? "",
  parentId: c.parentId ?? "",
  status: c.status ?? "active",
});

// Inventory → Category add/edit popup.
export const CategoryFormModal = ({ open, id, onClose }: CategoryFormModalProps) => {
  const [activeId, setActiveId] = useState<string | null>(id);
  const isEdit = !!activeId;

  const { data: existing } = useGetCategoryQuery(activeId ?? "", {
    skip: !activeId,
  });

  // Categories are scoped to the caller's organization on the server.
  const { data: allCategories = [] } = useGetCategoriesQuery();

  const [createCategory, { isLoading: creating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: updating }] = useUpdateCategoryMutation();

  const [form, setForm] = useState<CategoryForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const saving = creating || updating;

  // Re-sync to the record being opened every time the popup opens (or the
  // target id changes while open) — this component stays mounted between
  // opens, so state from a previous visit would otherwise linger.
  useEffect(() => {
    if (!open) return;
    setActiveId(id);
    setFormError(null);
    setFieldErrors({});
    if (id && existing && existing.id === id) {
      setForm(formFromCategory(existing));
    } else if (!id) {
      setForm(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  useEffect(() => {
    if (existing) setForm(formFromCategory(existing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updatedAt]);

  const setField = (key: keyof CategoryForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Parent options: other categories in the same org (exclude self).
  const parentOptions = allCategories.filter((c) => c.id !== activeId);

  const baseline = useMemo(
    () => (existing ? formFromCategory(existing) : emptyForm),
    [existing],
  );
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const handleSave = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = "Name is required.";
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      description: form.description.trim() || undefined,
      parentId: form.parentId || undefined,
      status: form.status,
    };

    try {
      if (isEdit && activeId) {
        await updateCategory({ id: activeId, input: payload }).unwrap();
      } else {
        await createCategory({ ...payload }).unwrap();
      }
      onClose();
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save category.",
      );
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit category" : "New category"}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving || (isEdit && !dirty)}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create category"}
          </button>
        </>
      }
    >
      <EntityMeta
        id={activeId ?? undefined}
        status={isEdit ? { label: form.status, tone: statusTone(form.status) } : undefined}
      />

      {formError ? <div className="form-error">{formError}</div> : null}

      <div className="form-grid">
        <Field label="Name *" error={fieldErrors.name}>
          <input
            value={form.name}
            onChange={(e) => {
              setField("name", e.target.value);
              setFieldErrors((prev) => (prev.name ? { ...prev, name: undefined } : prev));
            }}
          />
        </Field>
        <label className="field">
          Code
          <input
            value={form.code}
            onChange={(e) => setField("code", e.target.value)}
            placeholder="CAT-001"
          />
        </label>
        <label className="field">
          Parent category
          <select
            value={form.parentId}
            onChange={(e) => setField("parentId", e.target.value)}
          >
            <option value="">None (top level)</option>
            {parentOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Status
          <select
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
        <label className="field field--full">
          Description
          <textarea
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            placeholder="Short description…"
          />
        </label>
      </div>
    </Modal>
  );
};
